import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Field, useForm, useFormState } from 'react-final-form';
import CheckToggleField from 'components/CheckToggleField';
import { useTranslation } from 'react-i18next';
import { CreateDataRoomShareableLinkForm } from 'models/ShareableLink';
import DropzoneField from 'components/DropzoneField/DropzoneField.component';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import RadioGroupField from 'components/RadioGroupField';
import DataRoomNdaContext from 'router/subrouters/DataRoom/providers/DataRoomNda/DataRoomNda.context';
import ListingBanner from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/subrouters/ListingInformation/components/ManageListingVisibility/components/ListingBanner';
import ExclamationIcon from 'icons/Exclamation.icon';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import { isSingleElementArray } from 'utils';
import { INCLUDE_T_AND_C_NAME } from '../ShareableLinkTermsAndConditions/ShareableLinkTermsAndConditions.component';
import {
  DOCUMENT_FILES,
  SINGLE_FILE_MAX_SIZE_MB,
  SINGLE_FILE_MAX_SIZE_DIRECT_UPLOAD,
} from 'data/global';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './ShareableLinkNda.styles.scss';
import './ShareableLinkNda.styles.responsive.scss';

export const INCLUDE_NDA_NAME = 'includeNdaProtection';

const EXISTING_NDA_FIELD = 'dataRoomNdaId';

type ShareableLinkNdaProps = {
  className?: string;
};

const ShareableLinkNda: React.FC<ShareableLinkNdaProps> = (props) => {
  const { className } = props;

  const classes = classNames('shareable-link-nda', 'column', className);

  const { t } = useTranslation();

  const { popError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { dataRoomNdas } = useContext(DataRoomNdaContext);

  const {
    values: { includeNdaProtection, useExistingNda },
  } = useFormState<CreateDataRoomShareableLinkForm>();
  const { change } = useForm();

  const ndaOptions = useMemo(
    () => dataRoomNdas?.map(({ _id, name }) => ({ value: _id, label: name })),
    [dataRoomNdas],
  );

  const hasNdaOptions = useMemo(
    () => !!dataRoomNdas?.length,
    [dataRoomNdas?.length],
  );

  const uploadNDA: DropzoneUpload = useCallback(
    async (file, _, config) => {
      if (file.size > SINGLE_FILE_MAX_SIZE_DIRECT_UPLOAD) {
        popError(t('ndaTooLarge', { size: SINGLE_FILE_MAX_SIZE_MB }));
        return;
      }

      const dataRoomNda = await dataRoomApi.nda.uploadNda(
        dataRoomId,
        file,
        config,
      );

      return { ...dataRoomNda.file, name: file.name, _id: dataRoomNda?._id };
    },
    [dataRoomId, popError, t],
  );

  const deleteNda: DropzoneDelete = useCallback(async (file) => {
    await dataRoomApi.nda.deleteNda(file._id);

    return file._id;
  }, []);

  const customOnUseExistingNda = useCallback(
    (value: boolean) => {
      if (isSingleElementArray(ndaOptions))
        change(EXISTING_NDA_FIELD, value ? ndaOptions[0].value : undefined);
    },
    [ndaOptions, change],
  );

  const handleOnToogle = useCallback(
    (checked: boolean) => {
      if (checked) {
        change(INCLUDE_T_AND_C_NAME, false);
      }
    },
    [change],
  );

  return (
    <div className={classes}>
      <Field
        name={INCLUDE_NDA_NAME}
        type="checkbox"
        className="green-check-toggle"
        component={CheckToggleField}
        label={<span>{t('ndaProtection')}</span>}
        customOnChange={handleOnToogle}
      />

      {includeNdaProtection && (
        <>
          {hasNdaOptions && (
            <Field
              name="useExistingNda"
              component={CheckboxField}
              label={t('useExistingNda')}
              type="checkbox"
              customOnChange={customOnUseExistingNda}
            />
          )}

          {!useExistingNda && (
            <>
              <ListingBanner
                title={t('willNeedSomeTimeToProcessNda')}
                description={t('recipientsWontBeAbleToAccess')}
                icon={ExclamationIcon}
              />

              <Field
                name="dataRoomNda"
                accept={DOCUMENT_FILES}
                component={DropzoneField}
                maxFiles={1}
                showApplicationAsIframe
                dropMessage={t('pdfOrWord')}
                actionText={t('chooseFileOrDrag')}
                uploadRequest={uploadNDA}
                deleteRequest={deleteNda}
              />
            </>
          )}

          {hasNdaOptions && useExistingNda && (
            <RadioGroupField
              styled
              name={EXISTING_NDA_FIELD}
              label={t('chooseNda')}
              options={ndaOptions}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ShareableLinkNda;
